import { getGrid, type GridGeometry } from '../../grid';
import type { MapDefinition } from '../../model/gamestate/GameState';
import { type BoardChunk, RenderBoard, type WorldRect } from '../tilemap/board';
import { Viewport } from '../tilemap/viewport';
import type { World2DCamera } from './camera';
import { World2DTerrainLevelRenderer } from './World2DTerrainLevelRenderer';

const PERSISTENT_TERRAIN_LEVELS = [0.5, 0.72, 1, 1.18, 1.4, 1.65, 1.95, 2.3, 2.72, 3.21, 3.79, 4.48, 6] as const;
const PERSISTENT_TERRAIN_CHUNK_TILES = 10;
const PERSISTENT_TERRAIN_PRELOAD_CHUNKS = 1;
const PERSISTENT_TERRAIN_GUTTER_TILES = 1;
const MAX_PERSISTENT_TERRAIN_LEVELS = 5;
const MAX_PRESENTED_FALLBACK_LEVEL_DISTANCE = 1;

export interface TerrainSurfaceDebugStats {
  levelZoom: number;
  targetLevelZoom: number;
  usedResidentLevel: boolean;
  residentLevels: number;
  residentChunks: number;
  chunkTileSize: number;
  visibleChunks: number;
  rebuiltChunks: number;
  pendingTextureTiles: number;
  rebuildMs: number;
  drawMs: number;
  drawBlitCount: number;
  drawBlitArea: number;
  maxBlitArea: number;
  authoredMs: number;
  terrainBaseMs: number;
  squarePathMs: number;
  gridStrokeMs: number;
  pathTileLayerMs: number;
}

export interface TerrainGpuDrawCommand {
  key: string;
  texture: HTMLCanvasElement;
  sourceX: number;
  sourceY: number;
  sourceWidth: number;
  sourceHeight: number;
  destX: number;
  destY: number;
  destWidth: number;
  destHeight: number;
  needsUpload: boolean;
}

export type TerrainGpuPresentationMode = 'replace' | 'patch';

interface TerrainSurfaceLevel {
  readonly zoomFactor: number;
  readonly tileSize: number;
  readonly renderer: World2DTerrainLevelRenderer;
  readonly viewport: Viewport;
  readonly displayCanvas: HTMLCanvasElement;
  readonly displayCtx: CanvasRenderingContext2D | null;
  readonly scratchCanvas: HTMLCanvasElement;
  readonly scratchCtx: CanvasRenderingContext2D | null;
  retainedState: RetainedDisplayState | null;
  textured: boolean;
  pendingFullRedraw: boolean;
  pendingAssetRedrawFrames: number;
  lastUsedAt: number;
}

interface RetainedDisplayState {
  readonly levelZoom: number;
  readonly currentTileSize: number;
  readonly visibleWorldRect: WorldRect;
  readonly visibleChunkKeySignature: string;
}

function intersectWorldRects(a: WorldRect, b: WorldRect): WorldRect | null {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.right, b.right);
  const bottom = Math.min(a.bottom, b.bottom);
  if (right <= left || bottom <= top) {
    return null;
  }
  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
  };
}

function worldRectsOverlapOrTouch(a: WorldRect, b: WorldRect): boolean {
  return !(a.right < b.left || a.bottom < b.top || a.left > b.right || a.top > b.bottom);
}

function mergeWorldRects(a: WorldRect, b: WorldRect): WorldRect {
  const left = Math.min(a.left, b.left);
  const top = Math.min(a.top, b.top);
  const right = Math.max(a.right, b.right);
  const bottom = Math.max(a.bottom, b.bottom);
  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
  };
}

export class TerrainSurfaceManager {
  private map: MapDefinition;
  private viewportWidth = 1;
  private viewportHeight = 1;
  private devicePixelRatio = 1;
  private readonly levels = new Map<number, TerrainSurfaceLevel>();
  private readonly requestRender: () => void;
  private readonly requestTerrainInvalidate: (reason: string) => void;
  private readonly currentViewport = new Viewport(1);
  private activePresentationCanvas: HTMLCanvasElement | null = null;
  private activeGpuCommands: TerrainGpuDrawCommand[] | null = null;
  private activeGpuPresentationMode: TerrainGpuPresentationMode = 'replace';
  private pendingTexturedAssetFollowup = false;
  private presentedLevelZoom: number | null = null;
  private pendingPresentedLevelZoom: number | null = null;
  private currentBoard: RenderBoard | null = null;
  private currentBoardTileSize = -1;
  private levelUseCounter = 0;
  private prewarmTimer: number | null = null;
  private levelPromoteTimer: number | null = null;
  private texturedAssetFollowupTimer: number | null = null;
  private pendingPrewarmState: {
    targetZoomFactor: number;
    basePixelsPerWorldUnit: number;
    centerWorld: { x: number; y: number };
    currentTileSize: number;
  } | null = null;
  private lastDebugStats: TerrainSurfaceDebugStats = {
    levelZoom: 1,
    targetLevelZoom: 1,
    usedResidentLevel: true,
    residentLevels: 0,
    residentChunks: 0,
    chunkTileSize: PERSISTENT_TERRAIN_CHUNK_TILES,
    visibleChunks: 0,
    rebuiltChunks: 0,
    pendingTextureTiles: 0,

    rebuildMs: 0,
    drawMs: 0,
    drawBlitCount: 0,
    drawBlitArea: 0,
    maxBlitArea: 0,
    authoredMs: 0,
    terrainBaseMs: 0,
    squarePathMs: 0,
    gridStrokeMs: 0,
    pathTileLayerMs: 0,
  };

  constructor(
    map: MapDefinition,
    requestRender: () => void,
    requestTerrainInvalidate: (reason: string) => void,
  ) {
    this.map = map;
    this.requestRender = requestRender;
    this.requestTerrainInvalidate = requestTerrainInvalidate;
  }

  private cancelLevelPromote(): void {
    if (this.levelPromoteTimer !== null) {
      window.clearTimeout(this.levelPromoteTimer);
      this.levelPromoteTimer = null;
    }
    this.pendingPresentedLevelZoom = null;
  }

  private cancelTexturedAssetFollowup(): void {
    if (this.texturedAssetFollowupTimer !== null) {
      window.clearTimeout(this.texturedAssetFollowupTimer);
      this.texturedAssetFollowupTimer = null;
    }
    this.pendingTexturedAssetFollowup = false;
  }

  public destroy(): void {
    if (this.prewarmTimer !== null) {
      window.clearTimeout(this.prewarmTimer);
      this.prewarmTimer = null;
    }
    this.cancelLevelPromote();
    this.cancelTexturedAssetFollowup();
    for (const level of this.levels.values()) {
      level.renderer.destroy();
    }
    this.levels.clear();
  }

  public setMap(map: MapDefinition): void {
    this.map = map;
    if (this.prewarmTimer !== null) {
      window.clearTimeout(this.prewarmTimer);
      this.prewarmTimer = null;
    }
    this.cancelLevelPromote();
    this.cancelTexturedAssetFollowup();
    this.pendingPrewarmState = null;
    this.currentBoard = null;
    this.currentBoardTileSize = -1;
    this.presentedLevelZoom = null;
    for (const level of this.levels.values()) {
      this.invalidateLevelRetainedDisplay(level);
      level.textured = false;
      level.pendingFullRedraw = true;
      level.pendingAssetRedrawFrames = 0;
      level.renderer.setMap(map);
    }
  }

  public syncCanvasDimensions(width: number, height: number): void {
    this.viewportWidth = Math.max(1, Math.round(width));
    this.viewportHeight = Math.max(1, Math.round(height));
    this.devicePixelRatio =
      typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio)
        ? Math.max(1, window.devicePixelRatio)
        : 1;
    this.currentViewport.setContainerRect(new DOMRect(0, 0, this.viewportWidth, this.viewportHeight));
    for (const level of this.levels.values()) {
      level.renderer.syncCanvasDimensions(this.viewportWidth, this.viewportHeight);
      level.viewport.setContainerRect(new DOMRect(0, 0, this.viewportWidth, this.viewportHeight));
      this.syncLevelSurfaceCanvasSize(level);
    }
  }

  public getDebugStats(): TerrainSurfaceDebugStats {
    return this.lastDebugStats;
  }

  public invalidateAllLevelTextures(): void {
    for (const level of this.levels.values()) {
      level.renderer.invalidateMapTexture();
    }
  }

  public getPresentationCanvas(): HTMLCanvasElement | null {
    return this.activePresentationCanvas;
  }

  public getGpuDrawCommands(): readonly TerrainGpuDrawCommand[] | null {
    return this.activeGpuCommands;
  }

  public getGpuPresentationMode(): TerrainGpuPresentationMode {
    return this.activeGpuPresentationMode;
  }

  public draw(
    camera: World2DCamera,
    reason: string,
  ): void {
    this.activePresentationCanvas = null;
    this.activeGpuCommands = null;
    this.activeGpuPresentationMode = 'replace';
    const grid = getGrid(this.map.gridType);
    const currentZoomFactor = camera.getZoomFactor();
    const currentTileSize = camera.getPixelsPerWorldUnit();
    const basePixelsPerWorldUnit = currentTileSize / Math.max(0.0001, currentZoomFactor);
    const targetLevelZoom = this.selectNearestLevel(currentZoomFactor);
    let level = this.getBestAvailableLevel(targetLevelZoom, currentZoomFactor, basePixelsPerWorldUnit);
    let keepingPresentedLevel = false;
    if (reason === 'camera-center' && this.presentedLevelZoom !== null && this.presentedLevelZoom !== level.zoomFactor) {
      const presentedLevel = this.levels.get(this.presentedLevelZoom);
      if (presentedLevel?.textured && this.canKeepPresentedLevel(targetLevelZoom, presentedLevel.zoomFactor)) {
        level = presentedLevel;
        keepingPresentedLevel = true;
        if (this.pendingPresentedLevelZoom !== targetLevelZoom) {
          this.cancelLevelPromote();
          this.pendingPresentedLevelZoom = targetLevelZoom;
          this.levelPromoteTimer = window.setTimeout(() => {
            this.levelPromoteTimer = null;
            if (this.pendingPresentedLevelZoom !== targetLevelZoom) {
              return;
            }
            this.pendingPresentedLevelZoom = null;
            this.requestTerrainInvalidate('level-promote');
            this.requestRender();
          }, 120);
        }
      }
    }
    if (!keepingPresentedLevel && this.pendingPresentedLevelZoom !== null) {
      this.cancelLevelPromote();
    }
    this.touchLevel(level);

    camera.copyToViewport(this.currentViewport);
    const currentBoard = this.getCurrentBoard(grid, currentTileSize);
    this.syncLevelViewport(level, camera, grid, currentTileSize);

    const visibleChunks = level.renderer.prepareVisibleChunks(level.viewport, {
      preloadChunks: PERSISTENT_TERRAIN_PRELOAD_CHUNKS,
      gutterTiles: PERSISTENT_TERRAIN_GUTTER_TILES,
    });
    const visibleWorldRect = this.currentViewport.getVisibleWorldRect();
    const levelStats = level.renderer.getDebugStats();
    const drawStart = performance.now();
    let displayStats: { drawBlitCount: number; drawBlitArea: number; maxBlitArea: number };
    let finalLevelStats = levelStats;
    const forceAssetBootstrapRedraw = !level.textured && level.pendingAssetRedrawFrames > 0;
    if (!level.textured || forceAssetBootstrapRedraw) {
      level.renderer.drawBackground(level.viewport, {
        preloadChunks: PERSISTENT_TERRAIN_PRELOAD_CHUNKS,
      });
      finalLevelStats = level.renderer.getDebugStats();
      level.displayCtx?.clearRect(0, 0, this.viewportWidth, this.viewportHeight);
      level.displayCtx?.drawImage(
        level.renderer.getBackgroundCanvas(),
        0,
        0,
        this.viewportWidth,
        this.viewportHeight,
      );
      if (finalLevelStats.pendingTextureTiles === 0) {
        level.textured = true;
        level.pendingFullRedraw = false;
        level.retainedState = {
          levelZoom: level.zoomFactor,
          currentTileSize,
          visibleWorldRect: { ...visibleWorldRect },
          visibleChunkKeySignature: this.getVisibleChunkKeySignature(visibleChunks),
        };
      } else {
        this.invalidateLevelRetainedMetadata(level);
        this.requestTerrainInvalidate('asset-pending');
        this.requestRender();
      }
      if (level.pendingAssetRedrawFrames > 0) {
        level.pendingAssetRedrawFrames -= 1;
        if (level.pendingAssetRedrawFrames > 0) {
          this.requestTerrainInvalidate('asset-followup');
          this.requestRender();
        }
      }
      displayStats = {
        drawBlitCount: finalLevelStats.drawBlitCount,
        drawBlitArea: finalLevelStats.drawBlitArea,
        maxBlitArea: finalLevelStats.maxBlitArea,
      };
      this.activePresentationCanvas = level.displayCanvas;
    } else if (reason.endsWith('-priority')) {
      const commands = this.buildPriorityZoomCommands(level, currentBoard, visibleChunks, visibleWorldRect, currentTileSize, camera);
      this.activeGpuCommands = commands.commands;
      this.activeGpuPresentationMode = 'patch';
      displayStats = commands.stats;
    } else if (reason.startsWith('asset')) {
      const rebuiltRects = this.mergeWorldRectList(
        this.computeRebuiltWorldRects(
          currentBoard,
          visibleChunks,
          visibleWorldRect,
          finalLevelStats.rebuiltChunkKeys,
        ),
      );
      let assetRects = rebuiltRects;
      if (
        reason === 'asset'
        && rebuiltRects.length > 0
        && finalLevelStats.rebuiltChunkKeys.length > 0
      ) {
        const priorityRects = this.computePriorityZoomWorldRects(
          currentBoard,
          visibleChunks,
          visibleWorldRect,
          camera,
          currentTileSize,
        );
        const priorityRebuiltRects: WorldRect[] = [];
        for (const rebuiltRect of rebuiltRects) {
          for (const priorityRect of priorityRects) {
            const intersection = intersectWorldRects(rebuiltRect, priorityRect);
            if (intersection) {
              priorityRebuiltRects.push(intersection);
            }
          }
        }
        if (priorityRebuiltRects.length > 0) {
          assetRects = this.mergeWorldRectList(priorityRebuiltRects);
          if (!this.pendingTexturedAssetFollowup) {
            this.pendingTexturedAssetFollowup = true;
            this.cancelTexturedAssetFollowup();
            this.pendingTexturedAssetFollowup = true;
            this.texturedAssetFollowupTimer = window.setTimeout(() => {
              this.texturedAssetFollowupTimer = null;
              this.pendingTexturedAssetFollowup = false;
              this.requestTerrainInvalidate('asset-full');
              this.requestRender();
            }, 120);
          }
        }
      }
      const commands = this.buildCommandsForWorldRects(
        level,
        currentBoard,
        visibleChunks,
        visibleWorldRect,
        assetRects,
        new Set(finalLevelStats.rebuiltChunkKeys),
      );
      this.activeGpuCommands = commands.commands;
      this.activeGpuPresentationMode = 'patch';
      displayStats = commands.stats;
    } else {
      const commands = this.buildWorldRectDrawCommands(
        level,
        currentBoard,
        visibleChunks,
        visibleWorldRect,
        visibleWorldRect,
        new Set(finalLevelStats.rebuiltChunkKeys),
      );
      this.activeGpuCommands = commands.commands;
      this.activeGpuPresentationMode = 'replace';
      displayStats = commands.stats;
    }

    this.activePresentationCanvas = level.displayCanvas;

    const drawMs = performance.now() - drawStart;
    const retainZoomFactors = new Set<number>([
      level.zoomFactor,
      targetLevelZoom,
      ...this.collectAdjacentZoomFactors(targetLevelZoom),
    ]);
    this.evictLevels(retainZoomFactors);
    let residentChunks = 0;
    for (const candidateLevel of this.levels.values()) {
      residentChunks += candidateLevel.renderer.getResidentChunkCount();
    }
    this.lastDebugStats = {
      levelZoom: level.zoomFactor,
      targetLevelZoom,
      usedResidentLevel: level.zoomFactor !== targetLevelZoom,
      residentLevels: this.levels.size,
      residentChunks,
      chunkTileSize: finalLevelStats.chunkTileSize,
      visibleChunks: finalLevelStats.visibleChunks,
      rebuiltChunks: finalLevelStats.rebuiltChunks,
      pendingTextureTiles: finalLevelStats.pendingTextureTiles,
      rebuildMs: finalLevelStats.rebuildMs,
      drawMs,
      drawBlitCount: displayStats.drawBlitCount,
      drawBlitArea: displayStats.drawBlitArea,
      maxBlitArea: displayStats.maxBlitArea,
      authoredMs: finalLevelStats.authoredMs,
      terrainBaseMs: finalLevelStats.terrainBaseMs,
      squarePathMs: finalLevelStats.squarePathMs,
      gridStrokeMs: finalLevelStats.gridStrokeMs,
      pathTileLayerMs: finalLevelStats.pathTileLayerMs,
    };
    this.presentedLevelZoom = level.zoomFactor;
    this.schedulePrewarm({
      targetZoomFactor: targetLevelZoom,
      basePixelsPerWorldUnit,
      centerWorld: camera.getWorldCenter(),
      currentTileSize,
    });
  }

  private getOrCreateLevel(zoomFactor: number, basePixelsPerWorldUnit: number): TerrainSurfaceLevel {
    const existing = this.levels.get(zoomFactor);
    if (existing) {
      return existing;
    }

    const tileSize = basePixelsPerWorldUnit * zoomFactor;
    const backgroundCanvas = document.createElement('canvas');
    const displayCanvas = document.createElement('canvas');
    const displayCtx = displayCanvas.getContext('2d');
    const scratchCanvas = document.createElement('canvas');
    const scratchCtx = scratchCanvas.getContext('2d');
    displayCanvas.style.position = 'absolute';
    displayCanvas.style.inset = '0';
    displayCanvas.style.pointerEvents = 'none';
    displayCanvas.style.zIndex = '0';
    let level!: TerrainSurfaceLevel;
    const renderer = new World2DTerrainLevelRenderer(
      tileSize,
      this.map,
      backgroundCanvas,
      () => {
        const currentLevel = this.levels.get(zoomFactor);
        if (currentLevel && !currentLevel.textured) {
          currentLevel.pendingFullRedraw = true;
          this.invalidateLevelRetainedMetadata(currentLevel);
          currentLevel.pendingAssetRedrawFrames = Math.max(currentLevel.pendingAssetRedrawFrames, 2);
        }
        this.requestTerrainInvalidate('asset');
        this.requestRender();
      },
      {
        chunkTileSizeOptions: {
          minTilesPerChunk: PERSISTENT_TERRAIN_CHUNK_TILES,
          maxTilesPerChunk: PERSISTENT_TERRAIN_CHUNK_TILES,
          targetChunkPixelSize: tileSize * PERSISTENT_TERRAIN_CHUNK_TILES,
          preferredChunkSizes: [PERSISTENT_TERRAIN_CHUNK_TILES],
        },
      },
    );
    renderer.syncCanvasDimensions(this.viewportWidth, this.viewportHeight);
    const viewport = new Viewport(tileSize);
    viewport.setContainerRect(new DOMRect(0, 0, this.viewportWidth, this.viewportHeight));
    level = {
      zoomFactor,
      tileSize,
      renderer,
      viewport,
      displayCanvas,
      displayCtx,
      scratchCanvas,
      scratchCtx,
      retainedState: null,
      textured: false,
      pendingFullRedraw: true,
      pendingAssetRedrawFrames: 0,
      lastUsedAt: ++this.levelUseCounter,
    };
    this.syncLevelSurfaceCanvasSize(level);
    this.levels.set(zoomFactor, level);
    return level;
  }

  private getBestAvailableLevel(
    targetZoomFactor: number,
    currentZoomFactor: number,
    basePixelsPerWorldUnit: number,
  ): TerrainSurfaceLevel {
    const exactLevel = this.levels.get(targetZoomFactor);
    if (exactLevel?.textured) {
      return exactLevel;
    }
    if (this.levels.size === 0) {
      return this.getOrCreateLevel(targetZoomFactor, basePixelsPerWorldUnit);
    }

    let bestLevel: TerrainSurfaceLevel | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const level of this.levels.values()) {
      if (!level.textured && level !== exactLevel) {
        continue;
      }
      const distance = Math.abs(currentZoomFactor - level.zoomFactor);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestLevel = level;
      }
    }
    if (bestLevel) {
      return bestLevel;
    }
    return exactLevel ?? this.getOrCreateLevel(targetZoomFactor, basePixelsPerWorldUnit);
  }

  private getCurrentBoard(grid: GridGeometry, tileSize: number): RenderBoard {
    if (this.currentBoard && this.currentBoardTileSize === tileSize) {
      return this.currentBoard;
    }
    this.currentBoard = new RenderBoard(this.map, grid, tileSize, PERSISTENT_TERRAIN_CHUNK_TILES);
    this.currentBoardTileSize = tileSize;
    return this.currentBoard;
  }

  private buildWorldRectDrawCommands(
    level: TerrainSurfaceLevel,
    currentBoard: RenderBoard,
    visibleChunks: readonly BoardChunk[],
    viewportWorldRect: WorldRect,
    drawWorldRect: WorldRect,
    rebuiltChunkKeySet: ReadonlySet<string>,
  ): { commands: TerrainGpuDrawCommand[]; stats: { drawBlitCount: number; drawBlitArea: number; maxBlitArea: number } } {
    const commands: TerrainGpuDrawCommand[] = [];
    let drawBlitCount = 0;
    let drawBlitArea = 0;
    let maxBlitArea = 0;

    for (const sourceChunk of visibleChunks) {
      const currentChunk = currentBoard.getChunk(
        sourceChunk.chunkX,
        sourceChunk.chunkY,
        PERSISTENT_TERRAIN_GUTTER_TILES,
      );
      const intersection = intersectWorldRects(currentChunk.contentWorldBounds, drawWorldRect);
      if (!intersection) {
        continue;
      }
      const texture = level.renderer.getChunkTexture(sourceChunk.key);
      if (!texture) {
        continue;
      }

      const currentChunkWidth = Math.max(1, currentChunk.worldBounds.width);
      const currentChunkHeight = Math.max(1, currentChunk.worldBounds.height);
      const sourceTextureScaleX = texture.width / Math.max(1, sourceChunk.worldBounds.width);
      const sourceTextureScaleY = texture.height / Math.max(1, sourceChunk.worldBounds.height);
      const sourceLogicalX =
        ((intersection.left - currentChunk.worldBounds.left) / currentChunkWidth) * sourceChunk.worldBounds.width;
      const sourceLogicalY =
        ((intersection.top - currentChunk.worldBounds.top) / currentChunkHeight) * sourceChunk.worldBounds.height;
      const sourceLogicalWidth = (intersection.width / currentChunkWidth) * sourceChunk.worldBounds.width;
      const sourceLogicalHeight = (intersection.height / currentChunkHeight) * sourceChunk.worldBounds.height;
      const sourceX = sourceLogicalX * sourceTextureScaleX;
      const sourceY = sourceLogicalY * sourceTextureScaleY;
      const sourceWidth = sourceLogicalWidth * sourceTextureScaleX;
      const sourceHeight = sourceLogicalHeight * sourceTextureScaleY;

      commands.push({
        key: `${sourceChunk.key}:${intersection.left.toFixed(3)}:${intersection.top.toFixed(3)}:${intersection.right.toFixed(3)}:${intersection.bottom.toFixed(3)}`,
        texture,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        destX: intersection.left - viewportWorldRect.left,
        destY: intersection.top - viewportWorldRect.top,
        destWidth: intersection.width,
        destHeight: intersection.height,
        needsUpload: rebuiltChunkKeySet.has(sourceChunk.key),
      });

      const intersectionArea = intersection.width * intersection.height;
      drawBlitCount += 1;
      drawBlitArea += intersectionArea;
      if (intersectionArea > maxBlitArea) {
        maxBlitArea = intersectionArea;
      }
    }

    return {
      commands,
      stats: {
        drawBlitCount,
        drawBlitArea,
        maxBlitArea,
      },
    };
  }

  private buildPriorityZoomCommands(
    level: TerrainSurfaceLevel,
    currentBoard: RenderBoard,
    visibleChunks: readonly BoardChunk[],
    visibleWorldRect: WorldRect,
    currentTileSize: number,
    camera: World2DCamera,
  ): { commands: TerrainGpuDrawCommand[]; stats: { drawBlitCount: number; drawBlitArea: number; maxBlitArea: number } } {
    const priorityRects = this.computePriorityZoomWorldRects(currentBoard, visibleChunks, visibleWorldRect, camera, currentTileSize);
    if (priorityRects.length === 0) {
      return {
        commands: [],
        stats: {
          drawBlitCount: 0,
          drawBlitArea: 0,
          maxBlitArea: 0,
        },
      };
    }

    return this.buildCommandsForWorldRects(level, currentBoard, visibleChunks, visibleWorldRect, priorityRects, new Set());
  }

  private buildCommandsForWorldRects(
    level: TerrainSurfaceLevel,
    currentBoard: RenderBoard,
    visibleChunks: readonly BoardChunk[],
    viewportWorldRect: WorldRect,
    rects: readonly WorldRect[],
    rebuiltChunkKeySet: ReadonlySet<string>,
  ): { commands: TerrainGpuDrawCommand[]; stats: { drawBlitCount: number; drawBlitArea: number; maxBlitArea: number } } {
    const commands: TerrainGpuDrawCommand[] = [];
    let drawBlitCount = 0;
    let drawBlitArea = 0;
    let maxBlitArea = 0;
    for (const rect of rects) {
      const result = this.buildWorldRectDrawCommands(
        level,
        currentBoard,
        visibleChunks,
        viewportWorldRect,
        rect,
        rebuiltChunkKeySet,
      );
      commands.push(...result.commands);
      drawBlitCount += result.stats.drawBlitCount;
      drawBlitArea += result.stats.drawBlitArea;
      if (result.stats.maxBlitArea > maxBlitArea) {
        maxBlitArea = result.stats.maxBlitArea;
      }
    }
    return {
      commands,
      stats: {
        drawBlitCount,
        drawBlitArea,
        maxBlitArea,
      },
    };
  }

  private computeExposedWorldRects(visibleWorldRect: WorldRect, dx: number, dy: number): WorldRect[] {
    const rects: WorldRect[] = [];
    if (dx > 0) {
      rects.push({
        left: visibleWorldRect.left,
        top: visibleWorldRect.top,
        right: Math.min(visibleWorldRect.right, visibleWorldRect.left + dx),
        bottom: visibleWorldRect.bottom,
        width: Math.max(0, Math.min(visibleWorldRect.width, dx)),
        height: visibleWorldRect.height,
      });
    } else if (dx < 0) {
      rects.push({
        left: Math.max(visibleWorldRect.left, visibleWorldRect.right + dx),
        top: visibleWorldRect.top,
        right: visibleWorldRect.right,
        bottom: visibleWorldRect.bottom,
        width: Math.max(0, -dx),
        height: visibleWorldRect.height,
      });
    }
    if (dy > 0) {
      rects.push({
        left: visibleWorldRect.left,
        top: visibleWorldRect.top,
        right: visibleWorldRect.right,
        bottom: Math.min(visibleWorldRect.bottom, visibleWorldRect.top + dy),
        width: visibleWorldRect.width,
        height: Math.max(0, Math.min(visibleWorldRect.height, dy)),
      });
    } else if (dy < 0) {
      rects.push({
        left: visibleWorldRect.left,
        top: Math.max(visibleWorldRect.top, visibleWorldRect.bottom + dy),
        right: visibleWorldRect.right,
        bottom: visibleWorldRect.bottom,
        width: visibleWorldRect.width,
        height: Math.max(0, -dy),
      });
    }
    return rects.filter((rect) => rect.width > 0 && rect.height > 0);
  }

  private computeRebuiltWorldRects(
    currentBoard: RenderBoard,
    visibleChunks: readonly BoardChunk[],
    visibleWorldRect: WorldRect,
    rebuiltChunkKeys: readonly string[],
  ): WorldRect[] {
    if (rebuiltChunkKeys.length === 0) {
      return [];
    }
    const rebuiltKeySet = new Set(rebuiltChunkKeys);
    const rects: WorldRect[] = [];
    for (const sourceChunk of visibleChunks) {
      if (!rebuiltKeySet.has(sourceChunk.key)) {
        continue;
      }
      const currentChunk = currentBoard.getChunk(
        sourceChunk.chunkX,
        sourceChunk.chunkY,
        PERSISTENT_TERRAIN_GUTTER_TILES,
      );
      const intersection = intersectWorldRects(currentChunk.contentWorldBounds, visibleWorldRect);
      if (intersection) {
        rects.push(intersection);
      }
    }
    return rects;
  }

  private computePriorityZoomWorldRects(
    currentBoard: RenderBoard,
    visibleChunks: readonly BoardChunk[],
    visibleWorldRect: WorldRect,
    camera: World2DCamera,
    currentTileSize: number,
  ): WorldRect[] {
    const grid = getGrid(this.map.gridType);
    const centerTile = grid.pixelToTile(camera.getWorldCenter().x, camera.getWorldCenter().y, currentTileSize) as
      { q?: number; r?: number; x?: number; y?: number };
    const tileX = centerTile.q ?? centerTile.x ?? 0;
    const tileY = centerTile.r ?? centerTile.y ?? 0;
    const chunkTileSize = currentBoard.getChunkTileSize();
    const chunkX = Math.max(0, Math.floor(tileX / chunkTileSize));
    const chunkY = Math.max(0, Math.floor(tileY / chunkTileSize));
    const localX = tileX - (chunkX * chunkTileSize);
    const localY = tileY - (chunkY * chunkTileSize);
    const edgeThreshold = Math.min(2, Math.max(1, Math.floor(chunkTileSize / 4)));

    const candidateChunkKeys = new Set<string>();
    const addChunk = (x: number, y: number): void => {
      candidateChunkKeys.add(currentBoard.getChunkKey(x, y));
    };

    addChunk(chunkX, chunkY);

    const nearLeft = localX < edgeThreshold;
    const nearRight = localX >= chunkTileSize - edgeThreshold;
    const nearTop = localY < edgeThreshold;
    const nearBottom = localY >= chunkTileSize - edgeThreshold;

    if (nearLeft) {
      addChunk(chunkX - 1, chunkY);
    }
    if (nearRight) {
      addChunk(chunkX + 1, chunkY);
    }
    if (nearTop) {
      addChunk(chunkX, chunkY - 1);
    }
    if (nearBottom) {
      addChunk(chunkX, chunkY + 1);
    }
    if (nearLeft && nearTop) {
      addChunk(chunkX - 1, chunkY - 1);
    }
    if (nearRight && nearTop) {
      addChunk(chunkX + 1, chunkY - 1);
    }
    if (nearLeft && nearBottom) {
      addChunk(chunkX - 1, chunkY + 1);
    }
    if (nearRight && nearBottom) {
      addChunk(chunkX + 1, chunkY + 1);
    }

    const rects: WorldRect[] = [];
    for (const sourceChunk of visibleChunks) {
      if (!candidateChunkKeys.has(sourceChunk.key)) {
        continue;
      }
      const currentChunk = currentBoard.getChunk(
        sourceChunk.chunkX,
        sourceChunk.chunkY,
        PERSISTENT_TERRAIN_GUTTER_TILES,
      );
      const intersection = intersectWorldRects(currentChunk.contentWorldBounds, visibleWorldRect);
      if (intersection) {
        rects.push(intersection);
      }
    }
    return this.mergeWorldRectList(rects);
  }

  private syncLevelSurfaceCanvasSize(level: TerrainSurfaceLevel): void {
    const backingWidth = Math.max(1, Math.round(this.viewportWidth * this.devicePixelRatio));
    const backingHeight = Math.max(1, Math.round(this.viewportHeight * this.devicePixelRatio));
    for (const canvas of [level.displayCanvas, level.scratchCanvas]) {
      if (canvas.width === backingWidth && canvas.height === backingHeight) {
        continue;
      }
      canvas.width = backingWidth;
      canvas.height = backingHeight;
    }
    for (const ctx of [level.displayCtx, level.scratchCtx]) {
      if (!ctx) {
        continue;
      }
      ctx.setTransform(this.devicePixelRatio, 0, 0, this.devicePixelRatio, 0, 0);
      ctx.imageSmoothingEnabled = false;
    }
    level.pendingFullRedraw = true;
    this.invalidateLevelRetainedDisplay(level);
  }

  private syncLevelViewport(
    level: TerrainSurfaceLevel,
    camera: World2DCamera,
    grid: GridGeometry,
    currentTileSize: number,
  ): void {
    const centerWorld = camera.getWorldCenter();
    this.syncLevelViewportFromWorld(level, centerWorld, grid, currentTileSize);
  }

  private syncLevelViewportFromWorld(
    level: TerrainSurfaceLevel,
    centerWorld: { x: number; y: number },
    grid: GridGeometry,
    currentTileSize: number,
  ): void {
    const centerTile = grid.pixelToTile(centerWorld.x, centerWorld.y, currentTileSize);
    const currentTileOrigin = grid.tileToPixel(centerTile.q, centerTile.r, currentTileSize);
    const normalizedOffsetX = (centerWorld.x - currentTileOrigin.x) / currentTileSize;
    const normalizedOffsetY = (centerWorld.y - currentTileOrigin.y) / currentTileSize;
    level.viewport.setContainerRect(new DOMRect(0, 0, this.viewportWidth, this.viewportHeight));
    level.viewport.setTileSize(level.tileSize);
    const levelTileOrigin = grid.tileToPixel(centerTile.q, centerTile.r, level.tileSize);
    const levelWorldCenter = {
      x: levelTileOrigin.x + normalizedOffsetX * level.tileSize,
      y: levelTileOrigin.y + normalizedOffsetY * level.tileSize,
    };
    level.viewport.setCameraOffset(
      (this.viewportWidth / 2) - levelWorldCenter.x,
      (this.viewportHeight / 2) - levelWorldCenter.y,
    );
  }

  private schedulePrewarm(state: {
    targetZoomFactor: number;
    basePixelsPerWorldUnit: number;
    centerWorld: { x: number; y: number };
    currentTileSize: number;
  }): void {
    this.pendingPrewarmState = state;
    if (this.prewarmTimer !== null) {
      return;
    }
    this.prewarmTimer = window.setTimeout(() => {
      this.prewarmTimer = null;
      this.runPendingPrewarm();
    }, 0);
  }

  private runPendingPrewarm(): void {
    const state = this.pendingPrewarmState;
    if (!state) {
      return;
    }
    this.pendingPrewarmState = null;

    const grid = getGrid(this.map.gridType);
    const targetIndex = PERSISTENT_TERRAIN_LEVELS.indexOf(state.targetZoomFactor as (typeof PERSISTENT_TERRAIN_LEVELS)[number]);
    const zoomFactorsToPrepare = this.collectAdjacentZoomFactors(state.targetZoomFactor);

    let preparedNewLevel = false;
    for (const zoomFactor of zoomFactorsToPrepare) {
      const hadLevel = this.levels.has(zoomFactor);
      const level = this.getOrCreateLevel(zoomFactor, state.basePixelsPerWorldUnit);
      this.touchLevel(level);
      this.syncLevelViewportFromWorld(level, state.centerWorld, grid, state.currentTileSize);
      level.renderer.prepareVisibleChunks(level.viewport, {
        preloadChunks: PERSISTENT_TERRAIN_PRELOAD_CHUNKS,
        gutterTiles: PERSISTENT_TERRAIN_GUTTER_TILES,
      });
      if (!hadLevel) {
        preparedNewLevel = true;
      }
    }

    if (preparedNewLevel) {
      this.requestTerrainInvalidate('terrain-level-ready');
      this.requestRender();
    }
    this.evictLevels(new Set(zoomFactorsToPrepare));
  }

  private selectNearestLevel(zoomFactor: number): number {
    let bestLevel = PERSISTENT_TERRAIN_LEVELS[0];
    let bestDistance = Math.abs(zoomFactor - bestLevel);
    for (let index = 1; index < PERSISTENT_TERRAIN_LEVELS.length; index += 1) {
      const candidate = PERSISTENT_TERRAIN_LEVELS[index];
      const distance = Math.abs(zoomFactor - candidate);
      if (distance < bestDistance) {
        bestLevel = candidate;
        bestDistance = distance;
      }
    }
    return bestLevel;
  }

  private collectAdjacentZoomFactors(targetZoomFactor: number): number[] {
    const targetIndex = PERSISTENT_TERRAIN_LEVELS.indexOf(targetZoomFactor as (typeof PERSISTENT_TERRAIN_LEVELS)[number]);
    const zoomFactorsToPrepare: number[] = [];
    for (let offset = -1; offset <= 1; offset += 1) {
      const index = targetIndex + offset;
      if (index < 0 || index >= PERSISTENT_TERRAIN_LEVELS.length) {
        continue;
      }
      const zoomFactor = PERSISTENT_TERRAIN_LEVELS[index];
      if (!zoomFactorsToPrepare.includes(zoomFactor)) {
        zoomFactorsToPrepare.push(zoomFactor);
      }
    }
    return zoomFactorsToPrepare;
  }

  private canKeepPresentedLevel(targetZoomFactor: number, presentedZoomFactor: number): boolean {
    const targetIndex = PERSISTENT_TERRAIN_LEVELS.indexOf(targetZoomFactor as (typeof PERSISTENT_TERRAIN_LEVELS)[number]);
    const presentedIndex = PERSISTENT_TERRAIN_LEVELS.indexOf(presentedZoomFactor as (typeof PERSISTENT_TERRAIN_LEVELS)[number]);
    if (targetIndex < 0 || presentedIndex < 0) {
      return false;
    }
    return Math.abs(targetIndex - presentedIndex) <= MAX_PRESENTED_FALLBACK_LEVEL_DISTANCE;
  }

  private getVisibleChunkKeySignature(visibleChunks: readonly BoardChunk[]): string {
    return visibleChunks.map((chunk) => chunk.key).join('|');
  }

  private mergeWorldRectList(rects: readonly WorldRect[]): WorldRect[] {
    const merged: WorldRect[] = [];
    for (const rect of rects) {
      if (rect.width <= 0 || rect.height <= 0) {
        continue;
      }
      let nextRect = rect;
      let mergedAny = false;
      for (let index = 0; index < merged.length; index += 1) {
        const candidate = merged[index];
        if (!worldRectsOverlapOrTouch(candidate, nextRect)) {
          continue;
        }
        nextRect = mergeWorldRects(candidate, nextRect);
        merged.splice(index, 1);
        index -= 1;
        mergedAny = true;
      }
      merged.push(nextRect);
      if (mergedAny) {
        for (let index = 0; index < merged.length; index += 1) {
          for (let compareIndex = index + 1; compareIndex < merged.length; compareIndex += 1) {
            if (!worldRectsOverlapOrTouch(merged[index], merged[compareIndex])) {
              continue;
            }
            merged[index] = mergeWorldRects(merged[index], merged[compareIndex]);
            merged.splice(compareIndex, 1);
            compareIndex -= 1;
          }
        }
      }
    }
    return merged;
  }

  private invalidateLevelRetainedDisplay(level: TerrainSurfaceLevel): void {
    level.retainedState = null;
    level.displayCtx?.clearRect(0, 0, this.viewportWidth, this.viewportHeight);
    level.scratchCtx?.clearRect(0, 0, this.viewportWidth, this.viewportHeight);
  }

  private invalidateLevelRetainedMetadata(level: TerrainSurfaceLevel): void {
    level.retainedState = null;
  }

  private touchLevel(level: TerrainSurfaceLevel): void {
    level.lastUsedAt = ++this.levelUseCounter;
  }

  private evictLevels(retainZoomFactors: Set<number>): void {
    if (this.levels.size <= MAX_PERSISTENT_TERRAIN_LEVELS) {
      return;
    }
    const candidates = Array.from(this.levels.values())
      .filter((level) => !retainZoomFactors.has(level.zoomFactor))
      .sort((left, right) => left.lastUsedAt - right.lastUsedAt);
    for (const level of candidates) {
      if (this.levels.size <= MAX_PERSISTENT_TERRAIN_LEVELS) {
        break;
      }
      level.renderer.destroy();
      this.levels.delete(level.zoomFactor);
    }
  }
}
